/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */

import * as appStrings from '@touchplan/strings';
import { FormControl, FormGroup } from '@angular/forms';
import {
	defaultPrintConfiguration,
	PaperOrientations,
	PrintActions,
	PrintTextSizes,
	RoleLegendOptions,
} from '../../app-data/constants/plan-print.model';
import { IPaperSize, paperSizes, PaperTypes, PaperUnits } from 'src/app/app-data/constants/paper-sizes';

export interface INumberOption {
	label: string;
	value: number;
}

// DPI
export const DPI_OPTION_DEFAULT: number = 300;
export const DPI_OPTIONS: INumberOption[] = [
	{ label: '150', value: 150 },
	{ label: `${DPI_OPTION_DEFAULT}`, value: DPI_OPTION_DEFAULT },
	{ label: '600', value: 600 },
	{ label: '1200', value: 1200 },
];

// Orientation
export interface IOrientationOption {
	label: string;
	value: PaperOrientations;
}

export const ORIENTATION_OPTIONS: IOrientationOption[] = [
	{ label: appStrings.PLAN_PRINT_SETTINGS_ORIENTATION_LANDSCAPE, value: PaperOrientations.Landscape },
	{ label: appStrings.PLAN_PRINT_SETTINGS_ORIENTATION_PORTRAIT, value: PaperOrientations.Portrait },
];

// Role legend
export interface IRoleLegendOption {
	label: string;
	value: RoleLegendOptions;
}
export const ROLE_LEGEND_OPTIONS: IRoleLegendOption[] = [
	{ label: appStrings.PLAN_PRINT_SETTINGS_ROLE_LEGEND_NONE, value: RoleLegendOptions.None },
	{ label: appStrings.PLAN_PRINT_SETTINGS_ROLE_LEGEND_TOP_LEFT, value: RoleLegendOptions.TopLeft },
	{ label: appStrings.PLAN_PRINT_SETTINGS_ROLE_LEGEND_TOP_RIGHT, value: RoleLegendOptions.TopRight },
	{ label: appStrings.PLAN_PRINT_SETTINGS_ROLE_LEGEND_BOTTOM_LEFT, value: RoleLegendOptions.BottomLeft },
	{ label: appStrings.PLAN_PRINT_SETTINGS_ROLE_LEGEND_BOTTOM_RIGHT, value: RoleLegendOptions.BottomRight },
];

// Text size
export interface ITextSizeOption {
	label: string;
	value: PrintTextSizes;
}
export const TEXT_SIZE_OPTIONS: ITextSizeOption[] = [
	{ label: appStrings.PLAN_PRINT_SETTINGS_TEXT_SIZE_SMALL, value: PrintTextSizes.Small },
	{ label: appStrings.PLAN_PRINT_SETTINGS_TEXT_SIZE_MEDIUM, value: PrintTextSizes.Medium },
	{ label: appStrings.PLAN_PRINT_SETTINGS_TEXT_SIZE_LARGE, value: PrintTextSizes.Large },
];

// Units
export interface IUnitOption {
	label: string;
	value: PaperUnits;
}
export const UNIT_OPTIONS: IUnitOption[] = [
	{ label: appStrings.PLAN_PRINT_SETTINGS_UNITS_IN, value: PaperUnits.Inches },
	{ label: appStrings.PLAN_PRINT_SETTINGS_UNITS_PX, value: PaperUnits.Pixels },
];

// Page size
export interface PageSizeOption {
	key: PaperTypes;
	label: string;
	paperSize?: IPaperSize; // trust that all the keys are present but handle undefined just in case
}
export const PAGE_SIZE_CUSTOM = 'custom';

export const PAGE_SIZE_OPTIONS: PageSizeOption[] = [
	// ARCH series
	{
		key: PaperTypes.ArchA,
		label: appStrings.PLAN_PRINT_SETTINGS_PAGE_SIZE_ARCH_A,
		paperSize: paperSizes.get(PaperTypes.ArchA),
	},
	{
		key: PaperTypes.ArchB,
		label: appStrings.PLAN_PRINT_SETTINGS_PAGE_SIZE_ARCH_B,
		paperSize: paperSizes.get(PaperTypes.ArchB),
	},
	{
		key: PaperTypes.ArchC,
		label: appStrings.PLAN_PRINT_SETTINGS_PAGE_SIZE_ARCH_C,
		paperSize: paperSizes.get(PaperTypes.ArchC),
	},
	{
		key: PaperTypes.ArchD,
		label: appStrings.PLAN_PRINT_SETTINGS_PAGE_SIZE_ARCH_D,
		paperSize: paperSizes.get(PaperTypes.ArchD),
	},
	{
		key: PaperTypes.ArchE,
		label: appStrings.PLAN_PRINT_SETTINGS_PAGE_SIZE_ARCH_E,
		paperSize: paperSizes.get(PaperTypes.ArchE),
	},
	{
		key: PaperTypes.ArchE1,
		label: appStrings.PLAN_PRINT_SETTINGS_PAGE_SIZE_ARCH_E1,
		paperSize: paperSizes.get(PaperTypes.ArchE1),
	},

	// ANSI series
	{
		key: PaperTypes.AnsiA,
		label: appStrings.PLAN_PRINT_SETTINGS_PAGE_SIZE_ANSI_A,
		paperSize: paperSizes.get(PaperTypes.AnsiA),
	},
	{
		key: PaperTypes.AnsiB,
		label: appStrings.PLAN_PRINT_SETTINGS_PAGE_SIZE_ANSI_B,
		paperSize: paperSizes.get(PaperTypes.AnsiB),
	},
	{
		key: PaperTypes.AnsiC,
		label: appStrings.PLAN_PRINT_SETTINGS_PAGE_SIZE_ANSI_C,
		paperSize: paperSizes.get(PaperTypes.AnsiC),
	},
	{
		key: PaperTypes.AnsiD,
		label: appStrings.PLAN_PRINT_SETTINGS_PAGE_SIZE_ANSI_D,
		paperSize: paperSizes.get(PaperTypes.AnsiD),
	},
	{
		key: PaperTypes.AnsiE,
		label: appStrings.PLAN_PRINT_SETTINGS_PAGE_SIZE_ANSI_E,
		paperSize: paperSizes.get(PaperTypes.AnsiE),
	},

	// Common names
	{
		key: PaperTypes.Letter,
		label: appStrings.PLAN_PRINT_SETTINGS_PAGE_SIZE_US_LETTER,
		paperSize: paperSizes.get(PaperTypes.Letter),
	},
	{
		key: PaperTypes.Legal,
		label: appStrings.PLAN_PRINT_SETTINGS_PAGE_SIZE_US_LEGAL,
		paperSize: paperSizes.get(PaperTypes.Legal),
	},
	{
		key: PaperTypes.Tabloid,
		label: appStrings.PLAN_PRINT_SETTINGS_PAGE_SIZE_US_TABLOID,
		paperSize: paperSizes.get(PaperTypes.Tabloid),
	},
	{
		key: PaperTypes.Ledger,
		label: appStrings.PLAN_PRINT_SETTINGS_PAGE_SIZE_US_LEDGER,
		paperSize: paperSizes.get(PaperTypes.Ledger),
	},

	// A series
	{
		key: PaperTypes.A0,
		label: appStrings.PLAN_PRINT_SETTINGS_PAGE_SIZE_A0,
		paperSize: paperSizes.get(PaperTypes.A0),
	},
	{
		key: PaperTypes.A1,
		label: appStrings.PLAN_PRINT_SETTINGS_PAGE_SIZE_A1,
		paperSize: paperSizes.get(PaperTypes.A1),
	},
	{
		key: PaperTypes.A2,
		label: appStrings.PLAN_PRINT_SETTINGS_PAGE_SIZE_A2,
		paperSize: paperSizes.get(PaperTypes.A2),
	},
	{
		key: PaperTypes.A3,
		label: appStrings.PLAN_PRINT_SETTINGS_PAGE_SIZE_A3,
		paperSize: paperSizes.get(PaperTypes.A3),
	},
	{
		key: PaperTypes.A4,
		label: appStrings.PLAN_PRINT_SETTINGS_PAGE_SIZE_A4,
		paperSize: paperSizes.get(PaperTypes.A4),
	},

	// Custom option
	{
		key: PaperTypes.Custom,
		label: appStrings.PLAN_PRINT_SETTINGS_PAGE_SIZE_CUSTOM,
		paperSize: paperSizes.get(PaperTypes.Custom),
	},
];

// Form value
export interface IPlanPrintSettingsValue {
	orientation: PaperOrientations | null;
	roleLegend: RoleLegendOptions | null;
	textSize: PrintTextSizes | null;
	units: PaperUnits | null;
	dpi: number | null;
	pageSize: PaperTypes | null;
	width: number | null;
	height: number | null;
}

// Default form values
export const PLAN_PRINT_SETTINGS_DEFAULTS: IPlanPrintSettingsValue = {
	orientation: defaultPrintConfiguration.orientation,
	roleLegend: defaultPrintConfiguration.roleLegend,
	textSize: defaultPrintConfiguration.textSize,
	units: defaultPrintConfiguration.units,
	dpi: defaultPrintConfiguration.dpi,
	pageSize: defaultPrintConfiguration.pageSize,
	width: defaultPrintConfiguration.width,
	height: defaultPrintConfiguration.height,
};

// Form group
export interface IPlanPrintSettingsForm
	extends FormGroup<{
		orientation: FormControl<PaperOrientations | null>;
		roleLegend: FormControl<RoleLegendOptions | null>;
		textSize: FormControl<PrintTextSizes | null>;
		units: FormControl<PaperUnits | null>;
		dpi: FormControl<number | null>;
		pageSize: FormControl<PaperTypes | null>;
		width: FormControl<number | null>;
		height: FormControl<number | null>;
	}> {}

// TP-7266: From Slack, decision was made to support a minimum paper size of ISO 216 A5 standard: 5.8"x8.3"
export const MIN_WIDTH_INCHES = 5.8;
export const MIN_HEIGHT_INCHES = 8.3;
// TODO: bound the maximum size by the number of pixels as discovered from the conversation
// TP-7414: From slack, decision was made to support a maximum pixel size
// The image.io.BufferedImage can handle Integer.MAX_VALUE = 2,147,483,647 pixels with TYPE_INT_ARGB type.
// The combined width and height must be less than Integer.MAX_VALUE.
export const MAX_TOTAL_PIXELS = 2145000000;
